<template>
  <q-dialog
    :model-value="modelValue"
    persistent
    @update:model-value="emitClose"
  >
    <q-card class="modal-container">

      <!-- HEADER -->
      <q-card-section class="row items-center justify-between">
        <span class="modal-title">Redes e Subredes</span>
        <q-btn icon="close" flat round dense @click="close" />
      </q-card-section>

      <!-- BODY -->
      <q-card-section>

        <span class="midia-app-caption-sm">Pesquise uma Subrede</span>

        <!-- FILTRO REDE -->
        <div class="row items-center q-mt-sm">
          <q-input
            outlined
            dense
            class="col q-pr-sm"
            label="Rede *"
            model-value="Banco24Horas"
            readonly
          />
          <q-checkbox
            dense
            label="Selecionar Todas"
            disable
          />
        </div>

        <!-- FILTRO SUBREDE -->
        <div class="row items-center q-mt-sm">
          <q-input
            outlined
            dense
            class="col q-pr-sm"
            label="Subredes *"
            :model-value="selectedRows.length ? 'Selecionadas' : ''"
            readonly
          />
          <q-checkbox
            dense
            label="Selecionar Todas"
            v-model="selectAllSubnets"
          />
        </div>

        <!-- PESQUISA -->
        <div class="row items-center q-mt-md">
          <q-input
            outlined
            dense
            class="col q-pr-sm"
            placeholder="Pesquisar"
            v-model="search"
          >
            <template #prepend>
              <q-icon name="search" />
            </template>
          </q-input>

          <q-btn
            color="negative"
            label="Buscar"
            no-caps
          />
        </div>

        <!-- TABELA -->
        <q-table
          flat
          dense
          class="q-mt-md"
          row-key="id"
          :rows="filteredRows"
          :columns="columns"
          selection="multiple"
          v-model:selected="selectedRows"
          v-model:pagination="pagination"
        />

      </q-card-section>

      <!-- FOOTER ACTION -->
      <q-card-actions align="right">
        <q-btn
          color="negative"
          label="Continuar"
          no-caps
          @click="confirm"
        />
      </q-card-actions>

    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRedeSubredeStore } from '@/stores/redeSubredeStore'
import type { QTableColumn } from 'quasar'

const redeSubredeStore = useRedeSubredeStore()

const props = defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{ (e: 'update:modelValue', value: boolean): void }>()

/* ===== PAGINAÇÃO (IGUAL AO PROTÓTIPO) ===== */
const pagination = ref({
  page: 1,
  rowsPerPage: 5
})

/* ===== REFS ===== */
const search = ref('')
const selectAllSubnets = ref(false)
const selectedRows = ref<any[]>([])

/* ===== WATCHERS ===== */
watch(
  () => props.modelValue,
  async (open) => {
    if (open) {
      await redeSubredeStore.carregarSubredes()
      pagination.value.page = 1
    }
  }
)

watch(selectAllSubnets, (checked) => {
  if (checked) {
    selectedRows.value = [...filteredRows.value]
  } else {
    selectedRows.value = []
  }
})

/* ===== COMPUTEDS ===== */
const rows = computed(() =>
  redeSubredeStore.subredes.map(s => ({
    id: s.codSubredeAutoAtndto,
    codigo: s.codSubredeAutoAtndto,
    subrede: s.nomSubredeAutoAtndto,
    rede: s.nomRedeAutoAtndto
  }))
)

const filteredRows = computed(() => {
  if (!search.value) return rows.value

  const termo = search.value.toLowerCase()
  const isNumero = !isNaN(Number(search.value))

  return rows.value.filter(r => {
    const matchNome = r.subrede.toLowerCase().includes(termo)
    const matchCodigo = isNumero && r.codigo === Number(search.value)

    return matchNome || matchCodigo
  })
})

const columns: QTableColumn[] = [
  { name: 'codigo', label: 'Código', field: 'codigo', align: 'left' },
  { name: 'subrede', label: 'Subrede', field: 'subrede', align: 'left' },
  { name: 'rede', label: 'Rede', field: 'rede', align: 'left' }
]

const confirm = () => {
  const subredesFormatadas = selectedRows.value.map(row => ({
    codRedeAutoAtndto: 1, // Banco24Horas
    codSubredeAutoAtndto: row.codigo,
    nomRedeAutoAtndto: row.rede,
    nomSubredeAutoAtndto: row.subrede
  }))

  redeSubredeStore.selecionarSubredes(subredesFormatadas)
  emit('update:modelValue', false)
}

const close = () => emit('update:modelValue', false)
</script>

<style scoped>
.modal-container {
  min-width: 900px;
  max-width: 900px;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
}

:deep(.q-table thead th) {
  color: var(--q-negative);
  font-weight: 600;
}

:deep(.q-checkbox--truthy .q-checkbox__bg),
:deep(.q-checkbox--truthy .q-checkbox__check) {
  fill: var(--q-negative) !important;
}

:deep(.q-table__row--selected td) {
  font-weight: 500;
}

.footer-info {
  font-size: 12px;
}
</style>